import { CreateCustomer } from '../kingfisher/create-customer';
import { Address, Customer, Fidelity, HonorificPrefix, MarketingConsent } from '../kingfisher/customer';

/**
 * Contrat exposé par le BFF Castorama2D sur `/api/ciam/*`.
 *
 * Il est volontairement distinct des attributs JSON:API de GCI : la SPA dépend de ce contrat,
 * jamais de la forme de la gateway. Les noms ATG historiques (`loyaltySchemeName`,
 * `rawAddress`, …) ne doivent donc plus apparaître côté front.
 */
/** URL de retour remise à AuthApp, sur laquelle il renverra le client une fois authentifié. */
export function buildCiamCallbackUrl(origin: string, returnUrl: string): string {
  const callbackUrl = new URL(`${origin}/auth/callback`);
  callbackUrl.searchParams.set('returnUrl', returnUrl || '/');
  return callbackUrl.toString();
}

export interface CiamTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
}

export interface CiamTokenResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
}

export interface CiamAddress {
  houseNumber?: string;
  houseName?: string;
  addressLine1?: string;
  addressLine2?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

export interface CiamLoyaltyMembership {
  number?: string;
  name?: string;
}

export interface CiamMarketingChannel {
  channel?: string;
  optIn?: boolean;
}

export interface CiamCustomerProfile {
  email?: string;
  firstName?: string;
  lastName?: string;
  customerAtgId?: string;
  customerExternalId?: string;
  customerPingId?: string;
  customerId?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  honorificPrefix?: string;
  homeAddress?: CiamAddress;
  loyaltyMembership?: CiamLoyaltyMembership;
  marketingChannels?: CiamMarketingChannel[];
}

export interface CiamMarketingChannelUpdate {
  channel: string;
  optIn: boolean;
  submitted?: string;
  privacyPolicyVersionId?: string;
  marketingChannelVersionId?: string;
}

export interface CiamCustomerUpdate {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  honorificPrefix?: string;
  homeAddress?: CiamAddress;
  marketingChannels?: CiamMarketingChannelUpdate[];
}

/**
 * La session CIAM ne peut plus être renouvelée silencieusement : le cookie AuthApp est mort.
 * L'appelant doit proposer une reconnexion, sans détruire le travail en cours.
 */
export class CiamSessionExpiredError extends Error {
  constructor() {
    super('La session client a expiré.');
    this.name = 'CiamSessionExpiredError';
  }
}

/** Canaux de consentement connus, comparés sans tenir compte de la casse. */
const CONSENT_CHANNELS: Record<string, keyof MarketingConsent> = {
  EMAIL: 'email',
  PHONE: 'phone',
  POST: 'post',
  SMS: 'sms',
};

/**
 * HomeByMe attend un identifiant préfixé une seule fois. GCI peut renvoyer la valeur déjà
 * préfixée : on ne double donc jamais le préfixe.
 */
function withSqPrefix(value: string): string {
  return value.startsWith('SQ_') ? value : `SQ_${value}`;
}

/**
 * Priorité d'identifiant imposée par le contrat HomeByMe : `customerExternalId`, puis
 * `customerId`. `customerAtgId` et `customerPingId` ne doivent jamais servir de repli.
 */
function resolveCustomerId(profile: CiamCustomerProfile): string {
  const identifier = profile.customerExternalId?.trim() || profile.customerId?.trim();
  return identifier ? withSqPrefix(identifier) : '';
}

function mapAddress(address?: CiamAddress): Address | undefined {
  if (!address) {
    return undefined;
  }

  const mapped = new Address();
  mapped.num = address.houseNumber ?? '';
  mapped.complement = address.houseName ?? '';
  mapped.line1 = address.addressLine1 ?? '';
  mapped.line2 = address.addressLine2 ?? '';
  mapped.postalCode = address.postalCode ?? '';
  mapped.city = address.city ?? '';
  mapped.country = address.country ?? '';
  return mapped;
}

function mapLoyalty(loyalty?: CiamLoyaltyMembership): Fidelity | undefined {
  if (!loyalty?.number && !loyalty?.name) {
    return undefined;
  }

  const mapped = new Fidelity();
  mapped.number = loyalty.number;
  mapped.name = loyalty.name;
  return mapped;
}

function mapMarketingConsent(channels?: CiamMarketingChannel[]): MarketingConsent | undefined {
  if (!channels?.length) {
    return undefined;
  }

  const consent: MarketingConsent = { email: false, phone: false, post: false, sms: false };
  channels.forEach(channel => {
    const key = CONSENT_CHANNELS[(channel.channel ?? '').toUpperCase()];
    if (key) {
      consent[key] = !!channel.optIn;
    }
  });

  return consent;
}

export function mapCiamProfileToCustomer(profile: CiamCustomerProfile): Customer {
  const customer = new Customer();
  customer.id = resolveCustomerId(profile);
  customer.kfId = profile.customerId ?? '';
  customer.firstname = profile.firstName ?? '';
  customer.lastname = profile.lastName ?? '';
  customer.email = profile.email ?? '';
  customer.phoneNumber = profile.phoneNumber || undefined;
  customer.dateOfBirth = profile.dateOfBirth || undefined;
  customer.honorificPrefix = (profile.honorificPrefix as HonorificPrefix) || undefined;
  customer.address = mapAddress(profile.homeAddress);
  customer.fidelity = mapLoyalty(profile.loyaltyMembership);
  customer.marketingConsent = mapMarketingConsent(profile.marketingChannels);
  return customer;
}

/**
 * Champs sans lesquels la SPA n'a pas de client exploitable : `fullName`, les récapitulatifs
 * et les devis les affichent tous. AuthApp ne collecte que les credentials, donc une fiche
 * fraîchement créée revient de CIAM sans nom ni prénom : c'est ce manque qui déclenche la
 * popup de complétion, à chaque obtention de session tant qu'il n'est pas comblé.
 */
const REQUIRED_PROFILE_FIELDS = ['firstname', 'lastname'] as const;

export function isCustomerProfileIncomplete(customer: Customer | null | undefined): boolean {
  if (!customer) {
    return false;
  }

  return REQUIRED_PROFILE_FIELDS.some(field => !(customer[field] ?? '').trim());
}

function mapAddressToCiam(address?: Address): CiamAddress | undefined {
  if (!address) {
    return undefined;
  }

  return {
    houseNumber: address.num ?? '',
    houseName: address.complement ?? '',
    addressLine1: address.line1 ?? '',
    addressLine2: address.line2 ?? '',
    postalCode: address.postalCode,
    city: address.city?.replace('(', '').replace(')', ''),
    country: address.country,
  };
}

/**
 * Ne transmet que les attributs réellement collectés : le PATCH GCI est un patch, pas un
 * remplacement complet du profil.
 */
export function toCiamCustomerUpdate(customer: CreateCustomer): CiamCustomerUpdate {
  const update: CiamCustomerUpdate = {};

  if (customer.email) {
    update.email = customer.email;
  }
  if (customer.givenName) {
    update.firstName = customer.givenName;
  }
  if (customer.familyName) {
    update.lastName = customer.familyName;
  }
  if (customer.phoneNumber) {
    update.phoneNumber = customer.phoneNumber;
  }
  if (customer.dateOfBirth) {
    update.dateOfBirth = customer.dateOfBirth;
  }
  if (customer.honorificPrefix) {
    update.honorificPrefix = customer.honorificPrefix;
  }

  const address = mapAddressToCiam(customer.address);
  if (address) {
    update.homeAddress = address;
  }

  if (customer.marketingChannels?.length) {
    update.marketingChannels = customer.marketingChannels.map(channel => ({
      channel: channel.channel,
      optIn: channel.optIn,
      submitted: channel.submitted,
      privacyPolicyVersionId: channel.privacyPolicyVersionId,
      marketingChannelVersionId: channel.marketingChannelVersionId,
    }));
  }

  return update;
}
